/**
 * Touch-drag precedence for the studio canvases ("move first").
 *
 * The defect (Princewill, on his phone, 2026-11-04): touching a
 * tiny object - the MEMO text is the everyday case - and sliding
 * without lifting STRETCHES it instead of moving it. The mechanism
 * is fabric 5.5's own targeting: a press on an object that is
 * ALREADY selected checks the object's controls first
 * (`_findTargetCorner` with the generous touch corner size), and
 * on a tiny object the corner touch zones cover the whole body -
 * every touch lands "on a handle", so the gesture becomes a scale.
 * The natural phone flow hits it constantly: tap to select, then
 * touch again to drag the thing you just selected.
 *
 * The precedence rule implemented here (mobile-editor convention):
 * - A touch drag ALWAYS moves. fabric itself already drags when
 *   the object was not selected before the press (its corner
 *   action requires `alreadySelected`); this module covers the
 *   already-selected case.
 * - Scale/rotate by touch survives only where a finger can
 *   honestly be ON a handle: the target is big enough on screen
 *   that its corner zones do not swallow its body (min screen
 *   dimension >= TOUCH_HANDLE_MIN_BODY_PX), or the touch point is
 *   OUTSIDE the object's body quad (the protruding part of a
 *   corner zone, the rotate handle floating above the object) -
 *   even for a tiny object.
 * - Tiny target + touch inside the body quad => the gesture is a
 *   move, whatever corner fabric picked. Zooming in (pinch) makes
 *   the same object non-tiny on screen, so handles come back
 *   exactly when they become usable.
 * Mouse input never passes through here: desktop keeps fabric's
 * stock handle behaviour pixel for pixel.
 */

/** Fabric transform actions that resize/rotate (never a move). */
const SCALE_LIKE_ACTIONS = new Set(['scale', 'scaleX', 'scaleY', 'scaleEqui', 'rotate']);

/**
 * Minimum on-screen size (CSS px) of the target's SMALLER side
 * for touch scaling to be reachable at all. Below this, a
 * corner's touch zone (fabric's touchCornerSize square) covers
 * so much of the body that "on the handle" and "on the object"
 * are indistinguishable to a finger. 44px is the platform
 * minimum-touch-target convention.
 */
export const TOUCH_HANDLE_MIN_BODY_PX = 44;

export interface TouchDragDecisionInput {
    /** The transform action fabric chose for this press. */
    action: string | undefined | null;
    /** Target's rendered size in CSS px (post fit-zoom + pinch). */
    targetScreenW: number;
    targetScreenH: number;
    /** Touch point inside the target's body quad (canvas coords test). */
    pointerInBody: boolean;
}

/**
 * Decide whether a touch gesture fabric set up as a scale/rotate
 * must be rewritten into a drag. Returns 'drag' to rewrite, or
 * null to leave fabric's action untouched.
 */
export function resolveTouchDragAction(input: TouchDragDecisionInput): 'drag' | null {
    const action = input.action || '';
    if (!SCALE_LIKE_ACTIONS.has(action)) return null;
    const minSide = Math.min(input.targetScreenW, input.targetScreenH);
    if (!(minSide < TOUCH_HANDLE_MIN_BODY_PX)) return null; // handles are honestly reachable
    // Tiny target: a touch on the body is a move; only a touch on
    // the part of a handle zone that protrudes past the body may
    // still scale/rotate.
    return input.pointerInBody ? 'drag' : null;
}

/**
 * Empty-area gesture resolution (Princewill's gesture map,
 * 2026-11-04): a one-finger drag that starts on EMPTY canvas
 * pans the camera while the camera is zoomed in - at fit zoom
 * the same drag is fabric's marquee multi-select. The conflict
 * is resolved in favour of pan while zoomed: marquee is a
 * fit-zoom tool, and while zoomed the user's problem is
 * navigation, not selection. A drag starting on an object is
 * always fabric's (it moves the object - move-first).
 */
export function resolveEmptyAreaGesture(cameraScale: number, hasObjectHit: boolean): 'pan' | 'fabric' {
    if (hasObjectHit) return 'fabric';
    return cameraScale > 1 ? 'pan' : 'fabric';
}

/** Two taps this close together (ms, between their press starts) read as one double-tap. */
export const DOUBLE_TAP_WINDOW_MS = 400;
/** ...and this close on screen (CSS px, between their press points). */
export const DOUBLE_TAP_MAX_DIST_PX = 30;

export interface TapStamp { at: number; x: number; y: number }

/**
 * Does `next` complete a double-tap begun by `prev`? Both are
 * press stamps in client coords. A missing previous tap, a slow
 * second tap, or a tap somewhere else on the check is not one.
 */
export function doubleTapMatch(prev: TapStamp | null, next: TapStamp): boolean {
    if (!prev) return false;
    if (next.at - prev.at > DOUBLE_TAP_WINDOW_MS) return false;
    return Math.hypot(next.x - prev.x, next.y - prev.y) <= DOUBLE_TAP_MAX_DIST_PX;
}

/**
 * Held-finger stability: pointer travel (CSS px) at or below
 * this from the press point is tremor, not a gesture - the
 * wiring swallows those moves before fabric can turn them into
 * an object drag, so a stationary finger changes nothing until
 * it either becomes a real drag (> threshold), a long-press
 * (time), or lifts (a tap).
 */
export const STILL_FINGER_TOLERANCE_PX = 8;

export function isSubThresholdJitter(dx: number, dy: number): boolean {
    return Math.hypot(dx, dy) <= STILL_FINGER_TOLERANCE_PX;
}

/** Extra finger grab radius for fabric target finding, in CSS px. */
export const TOUCH_FIND_TOLERANCE_CSS_PX = 7;

/**
 * fabric's `targetFindTolerance` is expressed in canvas (logical)
 * px, so the CSS-px tolerance a finger should feel is divided by
 * the canvas's current display scale (CSS px per logical px,
 * i.e. element rect width / canvas width). Clamped so extreme
 * zooms keep it sane. Visible object padding stays 0 - this only
 * widens the invisible hit test while a touch press is landing.
 */
export function touchFindTolerance(displayScale: number): number {
    if (!(displayScale > 0) || !isFinite(displayScale)) return TOUCH_FIND_TOLERANCE_CSS_PX;
    const logical = Math.round(TOUCH_FIND_TOLERANCE_CSS_PX / displayScale);
    return Math.min(40, Math.max(4, logical));
}
